import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDiscussions, createDiscussion, getDiscussion, replyToDiscussion } from '../../services/discussions';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { ArrowLeft, MessageSquare, CheckCircle, ChevronDown } from 'lucide-react';

function DiscussionDetail({ discussion, onBack }) {
  const { user } = useAuth();
  const [full, setFull] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDiscussion(discussion.course_id, discussion.id).then(r => setFull(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [discussion]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    const r = await replyToDiscussion(discussion.id, reply);
    setFull(prev => ({ ...prev, replies: [...(prev.replies || []), r.data] }));
    setReply('');
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mt-12"></div>;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-blue-600 text-sm font-medium hover:text-blue-700 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Orqaga</button>
      <div className="bg-white rounded-2xl p-6 border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">{full?.user?.name?.charAt(0)}</div>
          <div>
            <span className="font-medium text-gray-900 text-sm">{full?.user?.name}</span>
            <span className="text-xs text-gray-400 ml-2">{full?.created_at && new Date(full.created_at).toLocaleDateString('uz')}</span>
          </div>
          {full?.is_resolved && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-auto flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Hal qilingan</span>}
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{full?.title}</h2>
        <p className="text-gray-600">{full?.body}</p>
      </div>

      <h3 className="font-bold text-gray-900">Javoblar ({full?.replies?.length || 0})</h3>
      {full?.replies?.map(r => (
        <div key={r.id} className={`bg-white rounded-xl p-4 border ${r.is_best_answer ? 'border-green-300 bg-green-50' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">{r.user?.name?.charAt(0)}</div>
            <span className="text-sm font-medium text-gray-900">{r.user?.name}</span>
            {r.is_best_answer && <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Eng yaxshi javob</span>}
          </div>
          <p className="text-sm text-gray-600 ml-10">{r.body}</p>
        </div>
      ))}

      <form onSubmit={handleReply} className="bg-white rounded-xl p-4 border border-gray-100">
        <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Javob yozing..." rows={3} className="w-full px-4 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-500 mb-3" />
        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">Javob yuborish</button>
      </form>
    </div>
  );
}

export default function Discussions() {
  const { courseId: paramCourseId } = useParams();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [activeCourseId, setActiveCourseId] = useState(paramCourseId || null);
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });

  // Load courses list when no courseId param
  useEffect(() => {
    if (paramCourseId) {
      setActiveCourseId(paramCourseId);
      return;
    }
    api.get('/courses').then(r => {
      const list = r.data?.data || r.data || [];
      setCourses(list);
      if (list.length > 0) setActiveCourseId(list[0].id);
    }).catch(() => {}).finally(() => { if (!paramCourseId) setLoading(false); });
  }, [paramCourseId]);

  // Load discussions for active course
  useEffect(() => {
    if (!activeCourseId) return;
    setLoading(true);
    getDiscussions(activeCourseId).then(r => setDiscussions(r.data?.data || r.data || [])).catch(() => setDiscussions([])).finally(() => setLoading(false));
  }, [activeCourseId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const r = await createDiscussion(activeCourseId, form);
    setDiscussions(prev => [r.data, ...prev]);
    setForm({ title: '', body: '' });
    setShowForm(false);
  };

  if (selected) return <DiscussionDetail discussion={{...selected, course_id: activeCourseId}} onBack={() => setSelected(null)} />;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><MessageSquare className="w-6 h-6" /> Muhokamalar</h1>
        {activeCourseId && <button onClick={() => setShowForm(!showForm)} className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">+ Yangi mavzu</button>}
      </div>

      {/* Course selector (when no courseId param) */}
      {!paramCourseId && courses.length > 0 && (
        <div className="relative">
          <select value={activeCourseId || ''} onChange={e => setActiveCourseId(Number(e.target.value))}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl outline-none focus:border-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 appearance-none pr-10">
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl p-5 border border-gray-100 space-y-3">
          <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Mavzu sarlavhasi" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-500" />
          <textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} placeholder="Savolingizni yozing..." rows={3} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-blue-500" />
          <div className="flex gap-2">
            <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">Yaratish</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200">Bekor qilish</button>
          </div>
        </form>
      )}

      {discussions.map(d => (
        <div key={d.id} onClick={() => setSelected(d)} className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-sm cursor-pointer transition">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{d.user?.name?.charAt(0)}</div>
            <span className="text-sm font-medium text-gray-900">{d.user?.name}</span>
            {d.is_resolved && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" /></span>}
          </div>
          <h3 className="font-bold text-gray-900 mb-1">{d.title}</h3>
          <p className="text-sm text-gray-500 line-clamp-2">{d.body}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {d.replies_count || 0} javob</span>
            <span>{new Date(d.created_at).toLocaleDateString('uz')}</span>
          </div>
        </div>
      ))}

      {discussions.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Hali muhokamalar yo'q</h3>
          <p className="text-gray-500">Birinchi bo'lib savol bering!</p>
        </div>
      )}
    </div>
  );
}